/**
 * MilestoneGateSection — the gate on a milestone (sheet 3 of docs/RULES_OF_FLOW.md; docs/GATES.md).
 *
 * Plainly: the list of things that must be in hand before this milestone starts. Only six milestone
 * types carry one (docs/GATES.md); every other type renders nothing here. Items are ticked "in hand"
 * or marked "not required" with a reason — that reason is the ONLY way past a missing item (Matt,
 * 2026-09-06: gates block, nobody waives a whole gate). "Cleared" is derived server-side from the
 * items, never set by hand.
 *
 * Self-contained section node in the MilestoneDocumentsSection mould: own lazy load into
 * ctx.gateCache[msId] (undefined = not started, null = in flight, false = this type has no gate,
 * {…} = loaded), outline rows only, ⋯ menus via ActionMenu. Wording rule: a high-schooler reads it.
 */

import { useEffect, useRef } from 'react'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { sectionKey } from '../outline/utils.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import { promptDialog, alertDialog } from './Dialog.jsx'
import { getMilestoneGate, setGateItemStatus, setGateItemNote, startGateWait, GATE_SOURCE_LABELS } from '../services/gates.js'
import { pickFiles, uploadReferenceDocument, openReferenceDocument, announceReferenceDocumentsChanged } from '../services/referenceDocuments.js'

function render() { window.dispatchEvent(new Event('outline-render')) }
function who() {
  const m = ctx.currentMember
  return { memberId: m?.id || null, memberName: m?.fields?.['Member Name'] || m?.fields?.['Name'] || null }
}
function fmtDate(ymd) {
  if (!ymd) return ''
  const d = new Date(ymd + 'T00:00:00')
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export default function MilestoneGateSection({ msId, readOnly = false, sectionZoom = false }) {
  const key = sectionKey(msId, 'gate')
  const isCollapsed = wf.collapsed[key] !== false
  const hasLoaded = useRef(false)
  const menu = useActionMenu()

  function maybeLoad() {
    if (hasLoaded.current) return
    hasLoaded.current = true
    if (!ctx.gateCache) ctx.gateCache = {}
    if (ctx.gateCache[msId] !== undefined) return
    ctx.gateCache[msId] = null
    getMilestoneGate(msId)
      .then(r => { if (ctx.gateCache) ctx.gateCache[msId] = r.gate || false; render() })
      .catch(() => { if (ctx.gateCache) ctx.gateCache[msId] = false; render() })
  }
  // The gate decides whether the milestone may start, so it loads as soon as the milestone is
  // opened — not only when this node is expanded — so the header can say CLEARED / NOT CLEARED.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { maybeLoad() }, [])
  // A file uploaded anywhere on this project (Reference > Documents, or another milestone's kit)
  // shows on this kit's rows too — reload when this project's documents change.
  useEffect(() => {
    function onDocs(e) {
      const g = ctx.gateCache ? ctx.gateCache[msId] : null
      if (!g || !e.detail || g.project_id !== e.detail.projectId) return
      getMilestoneGate(msId).then(r => { if (ctx.gateCache) ctx.gateCache[msId] = r.gate || false; render() }).catch(() => {})
    }
    window.addEventListener('reference-documents-changed', onDocs)
    return () => window.removeEventListener('reference-documents-changed', onDocs)
  }, [msId])

  const gate = ctx.gateCache ? ctx.gateCache[msId] : undefined
  if (gate === false) return null                       // this type has no gate — nothing to show
  if (gate === undefined || gate === null) {
    if (sectionZoom) return <div className="wf-list"><div className="wf-loading" style={{ color: 'var(--aj-text-muted)' }}>Loading…</div></div>
    return null                                         // no flicker of a header for a type that may have none
  }

  const items = gate.items || []
  const done = items.filter(i => i.status !== 'open').length
  const cleared = !!gate.cleared_at
  const pct = items.length ? Math.round(done / items.length * 100) : 0

  function apply(gateNext) { if (ctx.gateCache) ctx.gateCache[msId] = gateNext; render() }
  async function guard(fn) {
    try {
      const r = await fn(); if (r && r.gate) apply(r.gate)
      // The Documents list warns before a delete reopens a ticked item, so it needs the new tick state.
      if (ctx.refDocCache && gate.project_id && ctx.refDocCache[gate.project_id]) delete ctx.refDocCache[gate.project_id]
    }
    catch (e) { await alertDialog({ title: 'That did not save', message: String(e && e.message || e) }) }
  }
  const markInHand = (it) => guard(() => setGateItemStatus(it.id, 'complete', who()))
  const reopen     = (it) => guard(() => setGateItemStatus(it.id, 'open', who()))
  async function notRequired(it) {
    const reason = await promptDialog({ title: 'Not required on this project', message: 'Why does this project not need it? One sentence. It stays on the record, and it counts for every milestone on this project that needs the same thing.', placeholder: 'e.g. No HOA on this parcel', multiline: true, required: true })
    if (reason === null) return
    guard(() => setGateItemStatus(it.id, 'not_required', { ...who(), reason }))
  }
  const startClock = (it) => guard(() => startGateWait(it.id))
  async function editNote(it) {
    const note = await promptDialog({ title: it.note ? 'Edit the note' : 'Add a note', message: 'What the team should know about this answer: who decides, how to get on site. It shows under the item for every milestone that needs it.', defaultValue: it.note || '', multiline: true })
    if (note === null) return
    guard(() => setGateItemNote(it.id, note))
  }

  // Files for an item that arrives as one (docs/REFERENCE_DOCUMENTS.md). Uploading never ticks it.
  async function upload(it) {
    const files = await pickFiles()
    if (!files.length || !gate.project_id) return
    try {
      for (const file of files) await uploadReferenceDocument({ projectId: gate.project_id, file, factId: it.fact_id, ...who() })
      announceReferenceDocumentsChanged(gate.project_id)
    } catch (e) { await alertDialog({ title: 'The upload did not finish', message: String(e && e.message || e) }) }
  }


  const rows = items.map(it => <GateItemRow key={it.id} item={it} readOnly={readOnly} onInHand={markInHand} onNotRequired={notRequired} onReopen={reopen} onStartClock={startClock} onNote={editNote} onUpload={upload} />)

  const statusWord = cleared
    ? <span style={{ color: '#1D9E75', fontFamily: 'DM Mono, monospace', fontSize: 11, letterSpacing: '.06em', marginLeft: 10 }}>CLEARED</span>
    : <span style={{ color: '#c0392b', fontFamily: 'DM Mono, monospace', fontSize: 11, letterSpacing: '.06em', marginLeft: 10 }}>NOT CLEARED · {done} of {items.length} in hand</span>

  if (sectionZoom) {
    return (
      <div className="wf-list">
        <div style={{ padding: '4px 0 10px', fontSize: 13, color: 'var(--aj-text-mid)' }}>
          {gate.label}. {statusWord}
        </div>
        {rows}
      </div>
    )
  }

  return (
    <div className={`wf-node wf-node--section wf-node--section-gate${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" data-fn="zoomTo" data-level="section" data-sectionkey={key} title="Focus on the kit"></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Kit</span>
          <span style={{ color: 'var(--aj-text-mid)', marginLeft: 8, fontSize: 13 }}>{gate.label}</span>
          <span className="wf-meta">
            <span className="wf-cat-bar-wrap">
              <span className="wf-cat-bar-fill" style={{ width: pct + '%', background: cleared ? '#1D9E75' : pct > 0 ? '#1a6fa8' : 'var(--aj-border)' }}></span>
            </span>
          </span>
          {statusWord}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); ctx.expandCollapseDescendants(key, true) }}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => { menu.close(); ctx.expandCollapseDescendants(key, false) }}>Collapse all</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => { menu.close(); hasLoaded.current = false; if (ctx.gateCache) delete ctx.gateCache[msId]; maybeLoad() }}>Refresh</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && <div className="wf-children">{rows}</div>}
    </div>
  )
}

function GateItemRow({ item: it, readOnly, onInHand, onNotRequired, onReopen, onStartClock, onNote, onUpload }) {
  const menu = useActionMenu()
  const isDone = it.status === 'complete'
  const isNR = it.status === 'not_required' || it.status === 'went_without'
  const whoLabel = GATE_SOURCE_LABELS[it.source_kind] || it.source_kind
  const late = it.is_wait && it.expected_by && !isDone && !isNR && new Date(it.expected_by + 'T00:00:00') < new Date()
  return (
    <div className={`wf-node wf-node--item${isDone ? ' wf-node--done' : ''}${isNR ? ' wf-node--is-done' : ''}`} data-key={'gate-item:' + it.id}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ background: isDone ? '#1D9E75' : isNR ? 'var(--aj-border-mid)' : 'var(--wf-dot)' }}></span>
        <div className="wf-text-wrap" style={{ flexWrap: 'wrap', rowGap: 2 }}>
          <span className="wf-node-text" style={{ textDecoration: isDone || isNR ? 'line-through' : 'none', color: isDone || isNR ? 'var(--aj-text-muted)' : 'var(--aj-text)' }}>{it.name}</span>
          <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase', marginLeft: 8, color: it.source_kind === 'direction' ? '#b7791f' : 'var(--aj-text-muted)' }}>{whoLabel}</span>
          {isDone && it.completed_by_name && <span style={{ fontSize: 11.5, color: 'var(--aj-text-muted)', marginLeft: 8 }}>in hand · {it.completed_by_name}{it.from_milestone && !it.derived ? ' · on another milestone of this project' : ''}{it.went_ahead_first ? ' · work went ahead before it' + (it.went_ahead_first.reason ? ' (' + it.went_ahead_first.reason + ')' : '') : ''}</span>}
          {isNR && <span style={{ fontSize: 11.5, color: 'var(--aj-text-muted)', marginLeft: 8 }}>{it.status === 'went_without' ? 'went ahead without it' : 'not required'}{it.not_required_reason ? ': ' + it.not_required_reason : ''}{it.not_required_by_name ? ' · ' + it.not_required_by_name : ''}</span>}
          {it.note && <span style={{ flexBasis: '100%', fontSize: 12.5, color: 'var(--aj-text-mid)', fontStyle: 'italic' }}>{it.note}</span>}
          {it.is_wait && !isDone && !isNR && (
            it.started_at
              ? <span style={{ fontSize: 11.5, marginLeft: 8, color: late ? '#c0392b' : 'var(--aj-text-muted)' }}>clock started {fmtDate(it.started_at)} · expected by {fmtDate(it.expected_by)}{late ? ' · late' : ''}</span>
              : <span style={{ fontSize: 11.5, marginLeft: 8, color: 'var(--aj-text-muted)' }}>a wait · usually {it.lead_time_days} days{it.lead_time_safe_days ? `, up to ${it.lead_time_safe_days}` : ''}</span>
          )}
          {(it.documents || []).map(d => (
            <a key={d.id} href="#" title="Open the file" onClick={e => { e.preventDefault(); e.stopPropagation(); openReferenceDocument(d.id).catch(err => alertDialog({ title: 'Could not open it', message: String(err.message || err) })) }}
              style={{ fontSize: 11.5, marginLeft: 8, color: 'var(--aj-accent-blue)', textDecoration: 'none', whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', verticalAlign: 'bottom' }}>
              {'📎 ' + d.name}
            </a>
          ))}
          {it.expects_document && !(it.documents || []).length && !isDone && !isNR && (
            <span style={{ fontSize: 11.5, marginLeft: 8, color: 'var(--aj-text-muted)' }}>no file yet</span>
          )}
        </div>
        {!readOnly && <ActionMenuButton menu={menu} />}
      </div>
      {!readOnly && (
        <ActionMenu menu={menu}>
          {!isDone && <ActionMenuItem onClick={() => { menu.close(); onInHand(it) }}>In hand</ActionMenuItem>}
          {it.expects_document && <ActionMenuItem onClick={() => { menu.close(); onUpload(it) }}>{(it.documents || []).length ? 'Upload another file…' : 'Upload file…'}</ActionMenuItem>}
          {!isDone && !isNR && <ActionMenuItem onClick={() => { menu.close(); onNotRequired(it) }}>Not required on this project…</ActionMenuItem>}
          {it.is_wait && !isDone && !isNR && !it.started_at && <ActionMenuItem onClick={() => { menu.close(); onStartClock(it) }}>Clock started today</ActionMenuItem>}
          {/* A fact proven by a posted milestone (database/325) has nothing to put back; unpost that milestone instead. */}
          {(isDone || isNR) && !it.derived && <ActionMenuItem onClick={() => { menu.close(); onReopen(it) }}>Put it back</ActionMenuItem>}
          {isDone && it.derived && <ActionMenuItem onClick={() => menu.close()}>Read from the schedule: {it.completed_by_name}</ActionMenuItem>}
          {(isDone || isNR) && !it.derived && onNote && <ActionMenuItem onClick={() => { menu.close(); onNote(it) }}>{it.note ? 'Edit the note…' : 'Add a note…'}</ActionMenuItem>}
        </ActionMenu>
      )}
    </div>
  )
}
